
      import { createComponent } from '@lit/react'
      import { type EventName } from '@lit/react'
      import * as React from 'react'

      import Component from '../../components/navigation-menu/navigation-menu-item.component.js'
      import type { DdsNavigationItemClickEvent } from '../../events/events'
import type { DdsNavigationDropdownOpenEvent } from '../../events/events'
      export type { DdsNavigationItemClickEvent } from '../../events/events'
export type { DdsNavigationDropdownOpenEvent } from '../../events/events'

      const tagName = 'dap-ds-navigation-menu-item'

      /**
 * `dap-ds-navigation-menu-item`
 * @summary A navigation menu item that can contain either a simple link or dropdown content.
 * Supports nested items with cascading flyout behavior. Part of the navigation menu system.
 * This is for NAVIGATION (website menus), not application menus.
 *
 * @element dap-ds-navigation-menu-item
 * @title - Navigation Menu Item
 * @group navigation-menu
 *
 * @property {string} label - The label text for the navigation item. This will be displayed in the title slot if no custom title slot content is provided.
 * @property {'xs' | 'sm' | 'lg'} size - The size of the popup. Default is 'sm'.
 * @property {boolean} disabled - The disabled state of the popup. Default is false.
 * @property {boolean} opened - The open state of the popup. Default is false.
 * @property {'top' | 'right' | 'bottom' | 'left' | 'top-start' | 'top-end' | 'bottom-start' | 'bottom-end' | 'left-start' | 'left-end' | 'right-start' | 'right-end'} placement - The placement of the popup (automatically adjusted based on nesting level). Default is 'bottom-start'.
 * @property {'absolute' | 'fixed'} floatingStrategy - The floating strategy of the popup. Default is 'fixed'.
 * @property {number} offset - The offset of the popup. Default is 0.
 * @property {boolean} sync - Whether the popup should sync its width with the trigger. Default is false.
 * @property {number | 'auto'} maxHeight - The maximum height of the popup. Default is 250.
 * @property {number | 'auto'} maxWidth - The maximum width of the popup. Default is 'auto'.
 * @property {boolean} hasArrow - Whether the popup has an arrow. Default is false.
 * @property {boolean} overflow - Whether the popup should overflow. Default is true.
 * @property {boolean} fullWidth - Whether the popup should take full width of the screen. Default is false.
 * @property {boolean} exactHref - Whether the navigation item should be active if the href is exactly the same as the activeHref. Default is false.
 * @property {string} icon - The name of the icon to display in the trigger.
 * @property {string} ariaLabelledBy - The name of the element that labels the navigation dropdown.
 * @property {string} activeHref - The href of the navigation item that is active (receives aria-current="page").
 * @property {string} baseHref - The href of the navigation item. If provided, the navigation item will be active if the href is a substring of the activeHref.
 * @property {string} href - The href URL for the navigation item link. When provided, the default trigger renders as a link.
 * @property {'_blank' | '_self' | '_parent' | '_top'} target - The target attribute for link navigation.
 * @property {string} rel - The rel attribute for link security and behavior.
 * @property {number} level - The nesting level of this navigation item (0 = top-level, automatically set for nested items). Default is 0.
 * @property {'horizontal' | 'vertical'} orientation - The orientation of the parent navigation menu (automatically inherited). Default is 'horizontal'.
 * @property {'group' | 'items'} tabMode - Controls how Tab key navigates through top-level items. Default is 'group'.
 *
 * @event {{ href: string, event: Event }} dds-navigation-item-click - Fired when a navigation item is clicked.
 * @event {{ item: DapDSNavigationMenuItem }} dds-navigation-dropdown-open - Fired when a navigation dropdown is opened.
 *
 * @slot trigger - The trigger element (link, button, etc.) for this navigation item.
 * @slot title - The title of the navigation item.
 * @slot indicator - The expand indicator icon (defaults to arrow icon based on orientation and level).
 * @slot link - A router link element (e.g. Next.js Link) used for SPA navigation.
 *   Rendered outside the popup so it is always accessible to programmatic clicks.
 * @slot - The dropdown content (can contain nested dap-ds-navigation-menu-item elements).
 *
 * @csspart base - The base part of the navigation item.
 * @csspart trigger - The trigger element part.
 * @csspart trigger-base - The base part of the trigger button (from dap-ds-button).
 * @csspart trigger-content - The content part of the trigger button (from dap-ds-button).
 * @csspart trigger-high-contrast - The high contrast part of the trigger button (from dap-ds-button).
 * @csspart trigger-stack-base - The base part of the trigger stack (from dap-ds-stack).
 * @csspart title - The title slot wrapper.
 * @csspart indicator - The expand indicator container.
 * @csspart indicator-icon - The expand indicator icon part.
 * @csspart indicator-icon-base - The base part of the indicator icon (from dap-ds-icon).
 * @csspart indicator-icon-icon - The icon part of the indicator icon (from dap-ds-icon).
 * @csspart popup - The dropdown popup container.
 * @csspart content - The dropdown content wrapper (default slot).
 * @csspart arrow - The dropdown arrow part.
 *
 * @cssproperty --dds-navigation-menu-item-spacing - The padding/spacing of the navigation menu item content. (default: var(--dds-spacing-200)).
 * @cssproperty --dds-navigation-menu-item-border-width - The border width of the navigation menu item content. (default: var(--dds-border-width-base)).
 * @cssproperty --dds-navigation-menu-item-border-color - The border color of the navigation menu item content. (default: var(--dds-border-neutral-subtle)).
 * @cssproperty --dds-navigation-menu-item-border-radius - The border radius of the navigation menu item content. (default: var(--dds-radius-base)).
 * @cssproperty --dds-navigation-menu-item-background - The background color of the navigation menu item content. (default: var(--dds-background-neutral-base)).
 * @cssproperty --dds-navigation-menu-item-shadow - The box shadow of the navigation menu item content. (default: var(--dds-shadow-base)).
 *
 * @accessibility
 * - Active navigation items marked with `aria-current="page"`
 * - Dropdowns use `aria-expanded` and `aria-haspopup`
 * - No menu/menuitem roles (this is navigation, not an application menu)
 */
      const reactWrapper = createComponent({
        tagName,
        elementClass: Component,
        react: React,
        events: {
          onDdsNavigationItemClick: 'dds-navigation-item-click' as EventName<DdsNavigationItemClickEvent>,
onDdsNavigationDropdownOpen: 'dds-navigation-dropdown-open' as EventName<DdsNavigationDropdownOpenEvent>
        },
        displayName: "DapDSNavigationMenuItem"
      })

      export default reactWrapper
    